import AnimatedLoader from "../../../motionKit/AnimatedLoader";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Accordion from "../../../components/shared/Accordion";
import EmailThread, { ReplyBox } from "../../../components/shared/EmailThread";
import {
  fetchEmailThread, replyToEmailThread, registerWatchedThread, sendEmail, updateShipperDetails, emailTemplatePopulated, getListOfTemplates
} from "../../../Api";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { showSwal } from "../../../Helper/ShowAlert";
import TemplateSwitchConfirmModal from "../../../components/shared/TemplateSwitchConfirmModal";
import { formatMissingFieldLabel } from "../../../Helper/FilterationFunctions";
import { formatTemplateOptionLabel } from "../../../Helper/emailDisplayUtils";
import { EmailThreadSkeleton } from "../../../components/shared/SkeletonComponents";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import { sortThreadMessages } from "./threadOrdering";

// Helper function to parse email addresses from headers
const parseEmailAddresses = (headerValue) => {
  if (!headerValue) return [];
  return headerValue
    .split(',')
    .map(addr => {
      const trimmed = addr.trim();
      if (trimmed.includes("<")) {
        return trimmed.split("<")[1].replace(">", "").trim();
      }
      return trimmed;
    })
    .filter(addr => addr && addr.length > 0);
};

const parseEmailMessage = (message) => {
  const fromHeader = message.headers.from || "";
  const toHeader = message.headers.to || "";
  const ccHeader = message.headers.cc || "";
  const bccHeader = message.headers.bcc || "";

  // Numeric internalDate for combinedShipperMessages' sort comparator.
  const internalMs = parseInt(message.internalDate, 10);

  return {
    sender: fromHeader,
    email: fromHeader.includes("<")
      ? fromHeader.split("<")[1].replace(">", "")
      : fromHeader,
    to: parseEmailAddresses(toHeader),
    cc: parseEmailAddresses(ccHeader),
    bcc: parseEmailAddresses(bccHeader),
    subject: message.headers.subject,
    date: new Date(internalMs).toLocaleString(),
    // null, not 0: 0 is finite, so it passed the old sort guard and pinned the
    // message to epoch 1970 — the top of the thread. See threadOrdering.js.
    internalDate: Number.isFinite(internalMs) ? internalMs : null,
    html: message.body,
    messageId: message.headers.messageId,
    id: message.id,
    attachments: message.attachments || [],
  };
};

export default function ShipperConversation({
  shipperDetails,
  openPanel,
  setOpenPanel,
  panelTriggerKey,
  defaultShipperEmail,
  disabled,
  USER_EMAIL,
  defaultOpen,
  onFullscreen,
  isFullscreen,
  setRefetchTrigger,
}) {
  const loginToken = useSelector((state) => state.reducer.userToken);
  const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn);
  const [initialShipper, setInitialShipper] = useState(shipperDetails || {});
  const [emailMessages, setEmailMessages] = useState([]);
  const [scrollOnUpdate, setScrollOnUpdate] = useState(false);
  const [TO_EMAIL, setTO_EMAIL] = useState("");
  const [loading, setLoading] = useState(false);
  const [threadRefreshLoading, setThreadRefreshLoading] = useState(false);
  const [threadRefreshCooldown, setThreadRefreshCooldown] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  // Subscribe to SSE-delivered live messages for the active shipper thread so replies
  // surface without a manual refresh — matches CustomerConversation's pattern.
  const activeShipperThreadId = initialShipper?.shipperThreadId;
  const liveMessages = useSelector(
    (state) => state.threads.liveMessages?.[activeShipperThreadId] || []
  );
  // Pulse the Accordion header for ~2.8s whenever the live-messages length
  // grows, so the operator gets a visual cue that a new reply landed.
  const prevLiveLenRef = useRef(0);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    const len = liveMessages.length;
    if (len > prevLiveLenRef.current) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 2800);
      prevLiveLenRef.current = len;
      return () => clearTimeout(t);
    }
    prevLiveLenRef.current = len;
  }, [liveMessages.length]);
  const combinedShipperMessages = activeShipperThreadId
    ? sortThreadMessages([...emailMessages, ...liveMessages])
    : emailMessages;


  const [initialEmail, setInitialEmail] = useState("");
  const [initialSubject, setInitialSubject] = useState("");

  const updateShipperThreadId = async (threadId) => {
    try {
      const response = await updateShipperDetails({
        token: loginToken,
        shipperId: initialShipper._id,
        role: userLoggedIn?.role,
        payload: {
          shipperDetails: {
            ...initialShipper,
            _id: undefined,
            updatedAt: undefined,
            createdAt: undefined,
            shipperThreadId: threadId
          }
        },
      });
      setInitialShipper(response.shipper);
      showSwal('success', 'Shipper ThreadId updated!', 200);
    } catch (error) {
      console.error('Error updating shipper:', error);
      showSwal('error', 'Failed to update shipper');
    }
  };

  const bottomRef = useRef(null);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useLayoutEffect(() => {
    if (scrollOnUpdate) {
      isOpen && scrollToBottom();
      setScrollOnUpdate(false);
    }
  }, [emailMessages, scrollOnUpdate, isOpen]);

  const fetchAndSetThread = useCallback(async (opts = {}) => {
    const quiet = Boolean(opts?.quiet);
    if (!initialShipper?.shipperThreadId) return;
    if (quiet) setThreadRefreshLoading(true);
    else {
      setLoading(true);
      setErrorMsg("");
    }

    try {
      const res = await fetchEmailThread({
        token: loginToken,
        threadId: initialShipper.shipperThreadId,
        userEmail: USER_EMAIL,
        role: userLoggedIn?.role,
      });
      setTO_EMAIL(initialShipper?.email || "");
      const parsed = (res?.messages || []).map(parseEmailMessage);
      setEmailMessages(parsed);
      if (parsed.length > 0) {
        setSelectedTemplateName("");
        setTemplateData({});
      }
      // Auto-scroll disabled as latest messages are shown first
      setLastUpdated(new Date());
    } catch (err) {
      console.error("❌ Failed to fetch shipper thread:", err);
      if (quiet) {
        throw err;
      }
      const detail =
        err?.response?.data?.details ||
        err?.response?.data?.error ||
        err?.message ||
        "Unknown error";
      setErrorMsg(`Failed to load conversation: ${detail}`);
    } finally {
      if (quiet) setThreadRefreshLoading(false);
      else setLoading(false);
    }
  }, [loginToken, initialShipper?.shipperThreadId, initialShipper?.email, USER_EMAIL, userLoggedIn?.role]);

  const handleManualThreadRefresh = useCallback(async () => {
    if (!initialShipper?.shipperThreadId) return;
    if (threadRefreshLoading || threadRefreshCooldown) return;
    try {
      await fetchAndSetThread({ quiet: true });
      setRefetchTrigger?.((p) => p + 1);
      toast.success("Thread refreshed");
      setThreadRefreshCooldown(true);
      setTimeout(() => setThreadRefreshCooldown(false), 3000);
    } catch (e) {
      console.error("Could not refresh shipper email thread", e);
      const detail =
        e?.response?.data?.details ||
        e?.response?.data?.error ||
        e?.message ||
        "Unknown error";
      toast.error(`Could not refresh thread: ${detail}`);
    }
  }, [
    initialShipper?.shipperThreadId,
    threadRefreshLoading,
    threadRefreshCooldown,
    fetchAndSetThread,
    setRefetchTrigger,
  ]);

  const handleReplySubmit = async (replyHtml, attachments = [], recipients = {}) => {
    const latest = emailMessages?.[0];
    const allReferences = emailMessages
      .map((msg) => msg.messageId)
      .filter(Boolean)
      .reverse()
      .join(" ");

    try {
      // Ensure 'to' is an array with at least one valid email
      const toRecipients = Array.isArray(recipients.to)
        ? recipients.to.filter(email => email && email.trim())
        : (recipients.to ? [recipients.to] : [TO_EMAIL || defaultShipperEmail]);

      if (toRecipients.length === 0) {
        throw new Error('At least one recipient is required');
      }

      await replyToEmailThread({
        token: loginToken,
        threadId: initialShipper?.shipperThreadId,
        to: toRecipients,
        cc: Array.isArray(recipients?.cc) ? recipients.cc.filter(e => e && e.trim()) : [],
        bcc: Array.isArray(recipients?.bcc) ? recipients.bcc.filter(e => e && e.trim()) : [],
        subject: latest?.subject || null,
        body: replyHtml,
        from: USER_EMAIL,
        inReplyTo: latest?.messageId,
        role: userLoggedIn?.role,
        references: allReferences,
        attachments,
      });
      await fetchAndSetThread();
    } catch (err) {
      console.error("Failed to send reply", err);
      setErrorMsg("Failed to send email. Please try again.");
    }
  };

  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templateData, setTemplateData] = useState({});
  const [templatePopulating, setTemplatePopulating] = useState(false);
  const replyDraftDirtyRef = useRef(false);
  const onReplyDraftDirty = useCallback((dirty) => {
    replyDraftDirtyRef.current = dirty;
  }, []);
  const [templateSwitchModalOpen, setTemplateSwitchModalOpen] = useState(false);
  const [pendingTemplateName, setPendingTemplateName] = useState(null);
  const quoteId = useParams().id;

  const fetchTemplateContent = async (template) => {
    setTemplatePopulating(true);
    try {
      const templateResponse = await emailTemplatePopulated({
        token: loginToken,
        role: userLoggedIn?.role,
        quoteId,
        purpose: template.purpose,
        for: template.for,
      });
      let responseEmail = templateResponse.emailContent || {};
      responseEmail.message = responseEmail.message.replace(/href="N\/A"/g, `href="${openPanel.shipper.missingFields}"`);
      if (
        openPanel?.shipper?.missingFields?.length > 0 &&
        responseEmail.message?.includes("[Missing_Fields]")
      ) {
        const formattedMissingFields = openPanel.shipper.missingFields.map(field =>
          formatMissingFieldLabel(field)
        );
        const missingFieldsHtmlList = `
                  <p><strong>Missing Fields:</strong></p>
                  <ul>
                    ${formattedMissingFields.map(formattedField => `<li>${formattedField}</li>`).join('')}
                  </ul>
                `.replace(/<li> - /g, "<li>");
        const parts = responseEmail.message.split('[Missing_Fields]');
        responseEmail.message =
          parts[0] +
          missingFieldsHtmlList +
          (parts[1] ?? "");
      }
      if (emailMessages.length === 0) {
        setInitialSubject(responseEmail.subject);
        setInitialEmail(responseEmail.message);
      } else {
        setTemplateData(responseEmail);
      }
    } catch (error) {
      console.error("Failed to fetch template content", error);
    } finally {
      setTemplatePopulating(false);
    }
  };

  const handleTemplateChange = (event) => {
    const selectedName = event.target.value;
    if (selectedName === selectedTemplateName) return;
    if (replyDraftDirtyRef.current) {
      setPendingTemplateName(selectedName);
      setTemplateSwitchModalOpen(true);
      return;
    }
    setSelectedTemplateName(selectedName);
    const template = availableTemplates.find(t => t.templateName === selectedName);
    if (template) {
      fetchTemplateContent(template);
    }
  };

  const confirmPendingTemplateSwitch = () => {
    const name = pendingTemplateName;
    setPendingTemplateName(null);
    setTemplateSwitchModalOpen(false);
    if (!name) return;
    setSelectedTemplateName(name);
    const template = availableTemplates.find((t) => t.templateName === name);
    if (template) fetchTemplateContent(template);
  };

  const cancelPendingTemplateSwitch = () => {
    setPendingTemplateName(null);
    setTemplateSwitchModalOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const AllTemplates = await getListOfTemplates({
          token: loginToken,
          role: userLoggedIn?.role,
          search: "NS:|CS:",
          searchOnField: "templateName",
          for: "shipper"
        });

        const filteredTemplates = AllTemplates?.emailTemplates || [];
        setAvailableTemplates(filteredTemplates);

        if (openPanel?.shipper?.selectTemplate) {
          applyForcedTemplate(openPanel.shipper.selectTemplate, filteredTemplates);
        }
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setLoading(false);
      }
    };

    if (loginToken && quoteId) {
      fetchData();
    }
  }, [loginToken, quoteId, panelTriggerKey, defaultShipperEmail]);

  const applyForcedTemplate = (templatePattern, templates) => {
    const template = templates.find(t =>
      t.purpose == templatePattern
    );
    if (template) {
      setSelectedTemplateName(template.templateName);
      fetchTemplateContent(template);
    }
  };

  const handleInitialEmailSend = async (replyHtml, _attachments, recipients) => {
    if (!initialEmail || !initialSubject) {
      setErrorMsg("Subject and message are required.");
      return;
    }
    // ReplyBox.onSubmit signature is (text, attachments, recipients). Use the
    // operator-typed recipient if provided; fall back to the shipper's saved
    // email. Without this the backend rejects with "Missing required fields:
    // 'to' email address is required" when the saved shipper email is empty.
    const typedTo = Array.isArray(recipients?.to)
      ? recipients.to.find((v) => v && String(v).trim() !== "")
      : (typeof recipients?.to === "string" && recipients.to.trim()) || "";
    const toEmail =
      (typedTo && String(typedTo).trim()) ||
      initialShipper?.email ||
      defaultShipperEmail ||
      "";
    if (!toEmail) {
      setErrorMsg("Please enter the shipper's email address.");
      return;
    }
    setLoading(true);
    setErrorMsg("");

    try {
      const res = await sendEmail({
        token: loginToken,
        to: toEmail,
        from: USER_EMAIL,
        subject: initialSubject,
        role: userLoggedIn?.role,
        body: replyHtml || initialEmail,
        entityType: "shipper",
        // The shipper's NAME — see the transport pane for why the entity type
        // here rendered as "Shipper shipper replied".
        entityName:
          initialShipper?.name ||
          shipperDetails?.name ||
          "Shipper",
        quoteId,
      });

      if (res?.threadId) {
        updateShipperThreadId(res.threadId)
        await fetchAndSetThread();
      } else {
        throw new Error("Thread ID not returned");
      }
    } catch (err) {
      console.error("❌ Failed to send initial email:", err);
      setErrorMsg("Failed to start conversation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loginToken || !initialShipper?.shipperThreadId) return;

    const timeout = setTimeout(async () => {
      await fetchAndSetThread();

      try {
        await registerWatchedThread({
          token: loginToken,
          threadId: initialShipper.shipperThreadId,
          role: userLoggedIn?.role,
        });
      } catch (err) {
        console.error("Failed to register shipper thread for SSE:", err);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [loginToken, initialShipper?.shipperThreadId]);

  const templateSelectOptions =
    loading ? (
      <option value="">Loading templates...</option>
    ) : availableTemplates.length === 0 ? (
      <option value="">No templates available</option>
    ) : (
      availableTemplates.map((template) => (
        <option key={template._id} value={template.templateName}>
          {formatTemplateOptionLabel(template)}
        </option>
      ))
    );

  const templateLoadingOverlay = templatePopulating ? (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 backdrop-blur-sm dark:bg-[oklch(0.225_0.026_264/0.75)]"
      aria-busy="true"
      aria-label="Loading template"
    >
      <div className="flex flex-col items-center gap-2">
        <AnimatedLoader variant="docbuild" size={72} label="Loading template" />
        <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Loading template…</span>
      </div>
    </div>
  ) : null;

  /** Full-width template block above the thread composer (existing thread). */
  const shipperTemplateComposerAddon = (
    <div className="relative space-y-1.5">
      {templateLoadingOverlay}
      <label htmlFor="template-select-shipper" className="block text-xs font-semibold text-gray-700 dark:text-gray-200" style={{ lineHeight: "1.3" }}>
        Select Email Template:
      </label>
      <select
        id="template-select-shipper"
        className="w-full rounded border border-gray-300 bg-white px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none focus:shadow-outline dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-gray-200"
        value={selectedTemplateName}
        onChange={handleTemplateChange}
        disabled={loading || templatePopulating || availableTemplates.length === 0}
      >
        {templateSelectOptions}
      </select>
    </div>
  );

  /** Narrow template column for “initiate conversation” — same row as To inside ReplyBox (matches EmailThread merged bar). */
  const shipperInitialMergedToolbar = (
    <div className="w-[9.25rem] min-w-0 shrink-0 sm:w-40 lg:w-44 [&>div]:!space-y-0 [&>div]:relative [&_select]:box-border [&_select]:h-8 [&_select]:w-full [&_select]:min-w-0 [&_select]:max-w-full [&_select]:truncate [&_select]:rounded-md [&_select]:border [&_select]:border-slate-200/90 [&_select]:bg-white [&_select]:py-0 [&_select]:pl-2 [&_select]:pr-7 [&_select]:text-left [&_select]:text-[11px] dark:[&_select]:border-slate-600 dark:[&_select]:bg-slate-900/80">
      <div className="relative min-w-0 w-full">
        {templateLoadingOverlay}
        <label htmlFor="template-select-shipper-init" className="sr-only">
          Select email template
        </label>
        <select
          id="template-select-shipper-init"
          className="w-full"
          value={selectedTemplateName}
          onChange={handleTemplateChange}
          disabled={loading || templatePopulating || availableTemplates.length === 0}
        >
          {templateSelectOptions}
        </select>
      </div>
    </div>
  );

  return (
    <>
      <TemplateSwitchConfirmModal
        open={templateSwitchModalOpen}
        onConfirm={confirmPendingTemplateSwitch}
        onCancel={cancelPendingTemplateSwitch}
      />
    <Accordion
      title="Shipper Conversation"
      for="shipper"
      defaultOpen={disabled && defaultOpen}
      openPanel={openPanel}
      setOpenPanel={setOpenPanel}
      newMessageHeaderFlash={pulse}
      panelTriggerKey={panelTriggerKey}
      disabled={disabled}
      setOpen={setIsOpen}
      onFullscreen={onFullscreen}
      isFullscreen={isFullscreen}
      showFullscreenButton={true}
      tallBody
      fillParentHeight={Boolean(initialShipper?.shipperThreadId)}
      headerAction={
        initialShipper?.shipperThreadId ? (
          <button
            type="button"
            onClick={handleManualThreadRefresh}
            disabled={threadRefreshLoading || threadRefreshCooldown}
            title={
              threadRefreshCooldown && !threadRefreshLoading
                ? "Just refreshed"
                : "Refresh thread"
            }
            aria-label="Refresh thread"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200/90 bg-slate-50/90 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.8)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
          >
            <FontAwesomeIcon
              icon={threadRefreshLoading ? faCircleNotch : faRotateRight}
              className={`text-[12px] ${threadRefreshLoading ? "animate-spin" : ""}`}
            />
          </button>
        ) : null
      }
    >
      <div className="flex h-full min-h-0 min-w-0 flex-col">
        {errorMsg && <div className="mb-1.5 flex-shrink-0 text-xs text-red-600" style={{ lineHeight: '1.3' }}>{errorMsg}</div>}
        {initialShipper?.shipperThreadId ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {loading ? (
              <EmailThreadSkeleton messages={5} />
            ) : (
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <div className="mb-2 shrink-0 text-xs text-gray-500 dark:text-gray-400">
                  {combinedShipperMessages.length} message{combinedShipperMessages.length !== 1 && "s"}{" "}
                  {lastUpdated && `· Last updated ${lastUpdated.toLocaleTimeString()}`}
                </div>
                <EmailThread
                  messages={combinedShipperMessages}
                  templateData={templateData}
                  onReplySubmit={handleReplySubmit}
                  USER_EMAIL={USER_EMAIL}
                  order="asc"
                  bottomRef={bottomRef}
                  variant="flat"
                  roomyComposer
                  naturalStackHeight={false}
                  composerAddon={shipperTemplateComposerAddon}
                  conversationScrollKey={initialShipper?.shipperThreadId || ""}
                  onReplyDraftDirtyChange={onReplyDraftDirty}
                  collapsibleComposer
                  composerTourId="qd-reply-composer"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex-shrink-0 space-y-3">
            <div className="font-medium">Initiate Conversation With Shipper</div>
            <div className="flex items-center justify-between space-x-2">
              <label className="text-xs text-gray-600" style={{ lineHeight: '1.3' }}>Subject:</label>
              <input
                type="text"
                placeholder="Subject"
                value={initialSubject}
                onChange={(e) => setInitialSubject(e.target.value)}
                className="w-full rounded border p-2"
              />
            </div>
            {shipperTemplateComposerAddon}
            <ReplyBox
              value={initialEmail}
              onChange={setInitialEmail}
              onSubmit={handleInitialEmailSend}
              buttonText={loading ? "Sending..." : "Send Email"}
              isInitialSubmitType={true}
              messagesLength={0}
              initialRecipients={{
                to: (initialShipper?.email || defaultShipperEmail) ? [initialShipper?.email || defaultShipperEmail].filter(Boolean) : [],
                cc: [],
                bcc: []
              }}
              isExistingThread={false}
              USER_EMAIL={USER_EMAIL}
              variant="flat"
              mergedToolbar={shipperInitialMergedToolbar}
              recipientSyncKey={`${initialShipper?._id || ""}|${initialShipper?.email || defaultShipperEmail || ""}`}
            />
            <div ref={bottomRef} />
          </div>
        )}
      </div>
    </Accordion>
    </>
  );
}
